import { styled } from "@mui/material";
import { keyframes } from "@mui/system";
import { AnimationEvent, ReactNode, useEffect, useState } from "react";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";
import { AppRoutes } from "../../config";
import useAuthorization from "../../contexts/useAuthorization";

/* transitions.dev "Page side-by-side" (#08), incoming half, for swapping between the tabs.
   Wallet / Portal / Account are sibling routes, so the page being left has unmounted by
   the time the next one paints: there is no outgoing page to slide away, only the new one
   sliding in — from the right when the clicked tab sits right of the old one (forward),
   from the left when it sits left of it (back). Reads the --page-slide-* tunables App.tsx
   sets for the invite card, which runs the same recipe; as there, the recipe's blur lane
   is dropped (compositor-only: translate3d + opacity). */
const tabEnter = keyframes`
  0%   { opacity: 0; transform: translate3d(var(--tab-enter-from), 0, 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

/* Everything in the layout except the surface, plus everything on the surface except the
   tab row. index.tsx marks the surface with data-profile-surface. */
const TAB_ENTER_TARGETS =
  "&[data-tab-enter] > :not([data-profile-surface]), &[data-tab-enter] > [data-profile-surface] > :not(nav)";

/* Row geometry from the Figma: a 67px band of three equal thirds across the 1560px
   main, every corner a 20px radius. */
const TAB_ROW_HEIGHT = 67;
const CORNER = 20;

/* The page ground every panel floats on. Has to match the hard-coded #ccc in
   components/Layout (LayoutWrapper / ContentWrapper): the row paints this colour into
   the pinches between rounded corners, so anything else would read as a stain. */
const PAGE_GROUND = "#ccc";

/* One 20×20 tile of page ground with a quarter-disc cut out, centred on the given
   corner of the tile. Laid over a tab's rounded corner it fakes the concave pinch the
   Figma shows wherever two 20px radii meet. The transparent stop sits 1px inside the
   radius so the feather overlaps the tab's own anti-aliased edge instead of leaving a
   hairline of the panel colour showing through. */
const pinch = (at: string) =>
  `radial-gradient(circle at ${at}, transparent ${CORNER - 1}px, ${PAGE_GROUND} ${CORNER}px)`;

/* Tiles for the four corners of a tab, in background-layer order: top-left, top-right,
   bottom-left, bottom-right. The disc centre is always the tile corner that points
   into the tab, i.e. the centre of the tab's own 20px radius. */
const TOP_LEFT = pinch("100% 100%");
const TOP_RIGHT = pinch("0 100%");
const BOTTOM_LEFT = pinch("100% 0");
const BOTTOM_RIGHT = pinch("0 0");

type ProfileSurfaceVariant = "beige" | "light" | "image";

type TabEnter = "forward" | "back";

/* The click happens on the page being left, the entrance on the one being opened, so a tab
   click leaves its direction here for the next Profile to pick up as it mounts. Stamped, so
   a click whose page never mounted (a guard redirect, a ctrl-click into a new window) can't
   replay on some later visit. Browser back/forward and the mobile BottomNavBar don't go
   through it, and just show the page. */
let pendingEnter: { path: string; direction: TabEnter; at: number } | null = null;
const HANDOFF_MS = 1500;
/** Safety net for animationend never firing (e.g. the content is display:none mid-entrance). */
const SETTLE_MS = 1000;

const noteTabSwap = (path: string, from: number, to: number) => {
  // From a sub-page no tab is selected (from = -1), so there's no side to come in from.
  pendingEnter =
    from < 0 || from === to ? null : { path, direction: to > from ? "forward" : "back", at: performance.now() };
};

/** How this page was reached, if it was by a tab click; undefined once the entrance has played. */
const useTabEnter = () => {
  const { pathname } = useLocation();
  // Read on the first render so the attribute is there on the first paint — no frame of
  // the page at rest before it jumps to the keyframes' start.
  const [enter, setEnter] = useState<TabEnter | undefined>(() => {
    const pending = pendingEnter;
    if (!pending || performance.now() - pending.at > HANDOFF_MS) {
      return undefined;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    return matchPath({ path: pending.path, end: false }, pathname) ? pending.direction : undefined;
  });

  // Taken whether or not it was ours, so a remount (a page swapping its loading branch for
  // the loaded one) can't play it twice.
  useEffect(() => {
    pendingEnter = null;
  }, []);

  useEffect(() => {
    if (!enter) {
      return undefined;
    }
    const timer = window.setTimeout(() => setEnter(undefined), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [enter]);

  // Every animated child starts and ends on the same frame, so the first end is the end.
  // Dropping the attribute clears will-change and the resting translate3d (either would
  // make the content a containing block for fixed descendants — Portal's inline OrgPopUp);
  // the `both` fill already rests on the un-animated state, so nothing moves. Descendants'
  // own animationends bubble through here too, hence the name check.
  const onAnimationEnd = (e: AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === tabEnter.name) {
      setEnter(undefined);
    }
  };

  return { enter, onAnimationEnd };
};

const ProfileLayout = ({ children }: { children?: ReactNode }) => {
  const { enter, onAnimationEnd } = useTabEnter();
  return (
    <LayoutWrapper data-tab-enter={enter} onAnimationEnd={onAnimationEnd}>
      {children}
    </LayoutWrapper>
  );
};

/**
 * The Wallet / Portal / Account row. Which tabs exist depends on the role; which one is
 * selected comes from the route, matched with NavLink's default rule (the path or any
 * sub-path of it).
 *
 * `joined` means the row is sitting on a `ProfileSurface`: the selected tab is then
 * transparent so the panel colour (or hero image) shows through and runs straight into
 * the content below, and the outer tabs also round off the panel's top corners.
 */
const ProfileLinks = ({ joined }: { joined?: boolean }) => {
  const { isTrust, isAdmin, user } = useAuthorization();
  const { pathname } = useLocation();
  const { Dashboard, Verification, AdminPortal, Collection, Identification } = AppRoutes.Profile.subRoutes;

  const portal = isTrust ? Verification : isAdmin ? AdminPortal : user?.emailVerified ? Collection : null;
  const tabs = [
    ...(user?.emailVerified ? [{ label: "Wallet", to: Dashboard.path }] : []),
    ...(portal ? [{ label: "Portal", to: portal.path }] : []),
    { label: "Account", to: Identification.path },
  ];
  const activeIndex = tabs.findIndex((tab) => matchPath({ path: tab.to, end: false }, pathname) !== null);

  return (
    <Links aria-label="Profile sections" data-joined={joined || undefined}>
      {tabs.map((tab, i) => {
        const active = i === activeIndex;
        return (
          <Tab
            key={tab.to}
            to={tab.to}
            className={active ? "active" : undefined}
            aria-current={active ? "page" : undefined}
            onClick={() => noteTabSwap(tab.to, activeIndex, i)}
            // The tab next to the selected one leaves that bottom corner unpainted, so
            // the panel colour sweeps under it — the "flare" in the Figma.
            data-flare={i === activeIndex - 1 ? "right" : i === activeIndex + 1 ? "left" : undefined}
          >
            {tab.label}
          </Tab>
        );
      })}
    </Links>
  );
};

const LayoutWrapper = styled("div")`
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  & > div:last-of-type {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  /* The tab-swap entrance (see useTabEnter). What slides is the page's content: the
     surface itself stays put, so the tab row and the panel colour under it never move and
     only what sits on the panel (and anything below it) comes in from the side. */
  &[data-tab-enter="forward"] {
    --tab-enter-from: var(--page-slide-distance);
  }
  &[data-tab-enter="back"] {
    --tab-enter-from: calc(var(--page-slide-distance) * -1);
  }
  /* Content up to 8px past the right edge would otherwise flash a horizontal scrollbar.
     clip, not hidden: it makes no scroll container, so sticky descendants keep working. */
  &[data-tab-enter] {
    overflow-x: clip;
  }
  ${TAB_ENTER_TARGETS} {
    animation: ${tabEnter} var(--page-slide-dur) var(--page-slide-ease) both;
    will-change: opacity, transform;
  }
  @media (prefers-reduced-motion: reduce) {
    ${TAB_ENTER_TARGETS} {
      animation: none;
    }
  }
`;

/**
 * The panel a Profile page's first block of content lives on, with the tab row at its
 * top. Beige for the portals, the light grey for Account, and the land image (with the
 * hero's dark wash) for the Wallet — the selected tab is cut out of it, which is what
 * makes tab and panel read as one shape.
 *
 * Deliberately no z-index / isolation: Portal renders OrgPopUp inline, and a stacking
 * context here would trap its fixed overlay beneath the footer.
 */
const ProfileSurface = styled("div", {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "image",
})<{ variant: ProfileSurfaceVariant; image?: string }>(({ theme, variant, image }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  borderRadius: `${CORNER}px`,
  /* Same clipping the MUI Cards this replaces had, so square-cornered content can't
     poke out of the panel's corners. Fixed overlays are unaffected. */
  overflow: "hidden",

  ...(variant === "beige" && {
    backgroundColor: theme.palette.back.table,
    color: theme.palette.text.primary,
  }),
  ...(variant === "light" && {
    backgroundColor: theme.palette.back.infoCard,
    color: "#000",
  }),
  ...(variant === "image" && {
    backgroundColor: "#000",
    color: "#fff",
    backgroundImage: image
      ? `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url("${image}")`
      : "none",
    backgroundSize: "cover",
    backgroundPosition: "center",
  }),
}));

const Links = styled("nav")(({ theme }) => ({
  /* Below md the row is gone altogether — BottomNavBar takes over on mobile. */
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
  flexDirection: "row",
  alignItems: "stretch",
  width: "100%",
  height: TAB_ROW_HEIGHT,
  flex: "0 0 auto",
  /* No overflow clipping: the outer tabs' ::after tiles hang 20px below the row. */

  /* On a surface, an unselected outer tab also rounds off the panel's top corner
     beneath it — the Figma shows the ground pinching in between the tab's bottom
     corner and the panel's, not a square shoulder. Nothing to round when the outer
     tab is the selected one: the panel is continuous there. */
  "&[data-joined] > a:first-of-type:not(.active)::after, &[data-joined] > a:last-of-type:not(.active)::after": {
    content: '""',
    position: "absolute",
    bottom: -CORNER,
    width: CORNER,
    height: CORNER,
    pointerEvents: "none",
    zIndex: 1,
  },
  "&[data-joined] > a:first-of-type:not(.active)::after": {
    left: 0,
    backgroundImage: pinch("100% 100%"),
  },
  "&[data-joined] > a:last-of-type:not(.active)::after": {
    right: 0,
    backgroundImage: pinch("0 100%"),
  },
}));

const Tab = styled(RouterLink)(({ theme }) =>
  theme.unstable_sx({
    // Three equal slots, matching the Figma's 520px thirds across the 1560px main.
    flex: "1 1 0",
    minWidth: 0,
    position: "relative",
    display: "flex",
    alignItems: "center",
    px: "24px",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.2,
    border: "none",
    // Always a white card, so the label colour is fixed rather than themed.
    color: "#000",
    textDecoration: "underline",
    textUnderlineOffset: "2px",

    // Figma: an unselected tab is a white card floating on the page ground,
    // rounded on all four corners.
    backgroundColor: "#fff",
    borderRadius: `${CORNER}px`,
    transition: "background-color 150ms",
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },

    "&:hover": {
      backgroundColor: "#f3f3f3",
    },
    // Inset so the ring stays clear of the corner tiles and follows the radius.
    "&:focus-visible": {
      outline: "2px solid currentColor",
      outlineOffset: "-4px",
    },

    // The selected tab paints nothing of its own: the surface it sits on shows
    // through, which is what joins it to the panel below.
    "&.active": {
      backgroundColor: "transparent",
      color: "inherit",
      textDecoration: "none",
    },

    /* The pinches. A full-size overlay carrying one ground tile per corner; each tile
       is transparent inside the tab's own 20px radius and page ground outside it, so
       the convex corner appears to sit on the ground even though the panel colour is
       actually behind it. Only the 20×20 corner boxes are opaque and the label starts
       24px in, so it is never covered. */
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      pointerEvents: "none",
      zIndex: 1,
      backgroundRepeat: "no-repeat",
      backgroundSize: `${CORNER}px ${CORNER}px`,
      backgroundPosition: "0 0, 100% 0, 0 100%, 100% 100%",
      backgroundImage: [TOP_LEFT, TOP_RIGHT, BOTTOM_LEFT, BOTTOM_RIGHT].join(", "),
    },
    // Next to the selected tab the bottom corner on that side is left open — the
    // panel colour flares under the neighbour instead of the ground pinching in.
    '&[data-flare="left"]::before': {
      backgroundImage: [TOP_LEFT, TOP_RIGHT, "none", BOTTOM_RIGHT].join(", "),
    },
    '&[data-flare="right"]::before': {
      backgroundImage: [TOP_LEFT, TOP_RIGHT, BOTTOM_LEFT, "none"].join(", "),
    },
    // The selected tab only rounds its top corners; its bottom runs into the panel.
    // On an outer edge the tile paints outside the surface's own radius, i.e. over
    // ground that is already that colour, so it needs no special case.
    "&.active::before": {
      backgroundImage: [TOP_LEFT, TOP_RIGHT, "none", "none"].join(", "),
    },
  })
);

export { ProfileLinks, ProfileLayout, ProfileSurface };
export type { ProfileSurfaceVariant };
